import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import moment from "moment";

import { useTheme } from "@mui/material/styles";
import { ResponsiveBar } from "@nivo/bar";

import MUIBox from "@mui/material/Box";
import MUITypography from "@mui/material/Typography";

import Widget from "../../Widget";
import Format from "../../../Utility/Format";

/**
 * Sums follow-up counts for dates strictly before today. Used only on the
 * forecast column for the current day so overdue work appears as a separate
 * red segment stacked under that day.
 *
 * @param {object|null|undefined} followUpCounts Map of date keys (`YYYY-MM-DD`)
 *   to numeric counts. The `"null"` key is ignored.
 * @returns {number}
 */
function sumOverdueFollowupCountsBeforeToday(followUpCounts) {
  const followUpCountsSafeLookup = followUpCounts || {};
  const startOfTodayMoment = moment().startOf("day");
  let overdueFollowupCount = 0;

  for (const [followUpCountKey, rawFollowupCount] of Object.entries(
    followUpCountsSafeLookup,
  )) {
    if (typeof rawFollowupCount !== "number") {
      continue;
    }
    if (followUpCountKey === "null") {
      continue;
    }

    const followUpDateMoment = moment(followUpCountKey, "YYYY-MM-DD", true);
    if (followUpDateMoment.isValid() === false) {
      continue;
    }

    const isFollowUpDateBeforeToday = followUpDateMoment.isBefore(
      startOfTodayMoment,
      "day",
    );
    if (isFollowUpDateBeforeToday === true) {
      overdueFollowupCount += rawFollowupCount;
    }
  }

  return overdueFollowupCount;
}

/**
 * Builds the ordered list of day entries shown in the follow-up forecast bar
 * graph. The list starts at the current date and walks forward for a total of
 * `numberOfDaysInFollowupForecast` days. The first entry (today) also carries
 * the count of follow-ups that have no due date assigned (the `"null"` key on
 * `followUpCounts`) so the today column can be rendered as a stacked bar that
 * combines today's dated count with the no-due-date count.
 *
 * @param {object|null|undefined} widgetData The widget data.
 * @returns {Array<{dateKey: string, shortDayLabel: string, weekdayLabel: string, monthDayLabel: string, followupCount: number, nullDueDateFollowupCount: number, overdueFollowupCount: number, isToday: boolean}>}
 */
function buildFollowupCountsChartData(widgetData) {
  const followupCountsSafeLookup = widgetData?.followUpCounts || {};
  const nullDueDateFollowupCount =
    typeof followupCountsSafeLookup["null"] === "number"
      ? followupCountsSafeLookup["null"]
      : 0;
  const overdueFollowupCountForTodayBar = sumOverdueFollowupCountsBeforeToday(
    widgetData?.followUpCounts,
  );
  const followupCountsChartEntries = [];

  for (
    let dayOffsetFromToday = 0;
    dayOffsetFromToday < 7;
    dayOffsetFromToday += 1
  ) {
    const currentForecastMoment = moment()
      .startOf("day")
      .add(dayOffsetFromToday, "days");
    const currentForecastDateKey = currentForecastMoment.format("YYYY-MM-DD");
    const rawFollowupCountForDate =
      followupCountsSafeLookup[currentForecastDateKey];

    const followupCountForDate =
      typeof rawFollowupCountForDate === "number" ? rawFollowupCountForDate : 0;
    const isTodayEntry = dayOffsetFromToday === 0;

    followupCountsChartEntries.push({
      dateKey: currentForecastDateKey,
      shortDayLabel: currentForecastMoment.format("ddd M/D"),
      weekdayLabel: currentForecastMoment.format("ddd"),
      monthDayLabel: currentForecastMoment.format("M/D"),
      followupCount: followupCountForDate,
      nullDueDateFollowupCount: isTodayEntry ? nullDueDateFollowupCount : 0,
      overdueFollowupCount: isTodayEntry ? overdueFollowupCountForTodayBar : 0,
      isToday: isTodayEntry,
    });
  }

  return followupCountsChartEntries;
}

/**
 * Sums follow-up counts that should appear as the widget headline: every dated
 * bucket strictly before today (overdue), the bucket for today, and the
 * `"null"` no-due-date bucket. Future-dated buckets are excluded so the
 * headline stays aligned with overdue plus today plus unset due dates.
 *
 * @param {object|null|undefined} followUpCounts Map of date keys (`YYYY-MM-DD`),
 *   optional `"null"`, to numeric counts.
 * @returns {number}
 */
function sumFollowupCountsForOverdueTodayAndNoDueDate(followUpCounts) {
  const followUpCountsSafeLookup = followUpCounts || {};
  const startOfTodayMoment = moment().startOf("day");
  let totalFollowupCount = 0;

  for (const [followUpCountKey, rawFollowupCount] of Object.entries(
    followUpCountsSafeLookup,
  )) {
    if (typeof rawFollowupCount !== "number") {
      continue;
    }
    if (followUpCountKey === "null") {
      totalFollowupCount += rawFollowupCount;
      continue;
    }

    const followUpDateMoment = moment(followUpCountKey, "YYYY-MM-DD", true);
    if (followUpDateMoment.isValid() === false) {
      continue;
    }

    const isFollowUpDateBeforeToday = followUpDateMoment.isBefore(
      startOfTodayMoment,
      "day",
    );
    const isFollowUpDateToday = followUpDateMoment.isSame(
      startOfTodayMoment,
      "day",
    );
    if (isFollowUpDateBeforeToday === true || isFollowUpDateToday === true) {
      totalFollowupCount += rawFollowupCount;
    }
  }

  return totalFollowupCount;
}

/**
 * Sales queue dashboard widget: follow-ups due today for the current filters.
 *
 * @param {object} props
 * @param {object} props.queue Current queue record from context.
 * @param {array} props.prefilters Prefilter objects from the queue screen (same shape as `getQueueRows`).
 * @param {function} props.onReloadQueueRows Reloads queue rows and statistics after refresh.
 * @returns {React.ReactElement}
 */
export default function WidgetDashboardQueue(props) {
  const showFollowUpData =
    JSON.parse(props.dashboardWidget.jsonConfiguration).showFollowUpData !==
    false;

  useEffect(() => {
    if (props.isActive) {
      props.updateActiveWidgetConfiguration({
        dashboardConfiguration: {
          queueId: JSON.parse(props.dashboardWidget.jsonConfiguration).queue_id,
        },
      });
    }
  }, [props.dashboardWidget, props.isActive]);

  return (
    <Widget
      getWidgetId={() => props.widgetId}
      getTitle={() => props.dashboardWidget.name}
      isActive={() => props.isActive}
      useCompactWidth={showFollowUpData === false}
      getSubtitle={({ widgetData }) =>
        showFollowUpData
          ? `Total: ${Format.number(widgetData.totalInQueue)}`
          : null
      }
      onClick={() => props.onActiveWidgetChange()}
      renderVisual={({ widgetData }) => {
        if (widgetData && showFollowUpData) {
          return <WidgetDashboardFollowupChart widgetData={widgetData} />;
        }
      }}
      renderContent={({ widgetData }) => {
        if (widgetData) {
          const headlineCount = showFollowUpData
            ? sumFollowupCountsForOverdueTodayAndNoDueDate(
                widgetData.followUpCounts,
              )
            : widgetData.totalInQueue;
          return (
            <MUITypography
              component="div"
              variant="h5"
              color={headlineCount > 0 ? "" : "success.main"}
            >
              {Format.number(headlineCount)}
            </MUITypography>
          );
        }
      }}
    />
  );
}

/**
 * Renders a 7-day follow-up forecast bar graph inside the widget visual
 * region using Nivo. Each column represents one forecast day
 * and the column for today is visually emphasized so it stands out from
 * the rest of the forecast. The y-axis uses a fixed reference ceiling so
 * the absolute magnitude of each day's follow-up count drives the bar
 * height, but falls back to the largest count in the window when that
 * count exceeds the reference ceiling.
 *
 * @param {object} props
 * @param {object|null|undefined} props.widgetData Object keyed by
 * @returns {React.ReactElement}
 */
function WidgetDashboardFollowupChart(props) {
  const materialUiTheme = useTheme();
  const chartContainerElementReference = useRef(null);
  const [followupChartTooltipState, setFollowupChartTooltipState] = useState({
    isVisible: false,
    anchorLeftPixels: 0,
    anchorTopPixels: 0,
    tooltipTitleText: "",
    tooltipOverdueLabelText: "",
    tooltipPrimaryLabelText: "",
    tooltipSecondaryLabelText: "",
    tooltipDataIndex: null,
  });

  const followupCountsChartData = useMemo(
    () => buildFollowupCountsChartData(props.widgetData),
    [props.widgetData],
  );

  const largestStackedFollowupTotalInWindow = useMemo(() => {
    return followupCountsChartData.reduce(
      (currentLargestStackedTotal, followupCountsChartEntry) => {
        const stackedTotalForEntry =
          followupCountsChartEntry.followupCount +
          followupCountsChartEntry.nullDueDateFollowupCount +
          followupCountsChartEntry.overdueFollowupCount;
        return Math.max(currentLargestStackedTotal, stackedTotalForEntry);
      },
      0,
    );
  }, [followupCountsChartData]);

  const followupCountChartYAxisSuggestedMaximum = Math.max(
    20,
    largestStackedFollowupTotalInWindow,
  );

  const followupCountsBarChartData = useMemo(() => {
    // Render a tiny sliver (~2% of the chart ceiling) for columns whose
    // entire stacked total is zero so the column is still visible as a
    // baseline hint instead of appearing empty or missing. The real
    // count is still shown in the hover tooltip because that reads from
    // `followupCountsChartData`.
    const zeroCountDisplayFloorValue =
      followupCountChartYAxisSuggestedMaximum * 0.02;

    // When a day has no follow-ups at all, show a tiny baseline using the
    // dated ("Orders") segment so the column is still visible. Do not put
    // that floor on the "No due date" stack: that dataset uses the primary
    // palette color and would incorrectly tint every empty future day.
    const nullDueDateFollowupCountsBarDisplayValues =
      followupCountsChartData.map((followupCountsChartEntry) => {
        const stackedTotalForEntry =
          followupCountsChartEntry.followupCount +
          followupCountsChartEntry.nullDueDateFollowupCount +
          followupCountsChartEntry.overdueFollowupCount;
        if (stackedTotalForEntry === 0) {
          return 0;
        }
        return followupCountsChartEntry.nullDueDateFollowupCount;
      });
    const datedFollowupCountsBarDisplayValues = followupCountsChartData.map(
      (followupCountsChartEntry) => {
        const stackedTotalForEntry =
          followupCountsChartEntry.followupCount +
          followupCountsChartEntry.nullDueDateFollowupCount +
          followupCountsChartEntry.overdueFollowupCount;
        if (stackedTotalForEntry === 0) {
          return zeroCountDisplayFloorValue;
        }
        return followupCountsChartEntry.followupCount;
      },
    );
    const overdueFollowupCountsBarDisplayValues = followupCountsChartData.map(
      (followupCountsChartEntry) =>
        followupCountsChartEntry.overdueFollowupCount,
    );

    return followupCountsChartData.map(
      (followupCountsChartEntry, chartDataIndex) => ({
        weekday: followupCountsChartEntry.weekdayLabel,
        noDueDate: nullDueDateFollowupCountsBarDisplayValues[chartDataIndex],
        orders: datedFollowupCountsBarDisplayValues[chartDataIndex],
        overdue: overdueFollowupCountsBarDisplayValues[chartDataIndex],
      }),
    );
  }, [followupCountsChartData, followupCountChartYAxisSuggestedMaximum]);

  const handleColumnPointerEnter = (hoveredColumnIndex, hoverPointerEvent) => {
    const chartContainerElement = chartContainerElementReference.current;
    if (chartContainerElement === null || chartContainerElement === undefined) {
      return;
    }

    const tooltipChartEntry = followupCountsChartData[hoveredColumnIndex];
    if (tooltipChartEntry === undefined) {
      return;
    }

    const datedFollowupCountForTooltip = tooltipChartEntry.followupCount;
    const tooltipTitleText = tooltipChartEntry.shortDayLabel;
    const tooltipPrimaryLabelText =
      tooltipChartEntry.isToday === true
        ? `${Format.number(datedFollowupCountForTooltip)} Today`
        : `${Format.number(datedFollowupCountForTooltip)} Due ${tooltipChartEntry.weekdayLabel}`;

    const overdueFollowupCountForTooltip =
      tooltipChartEntry.overdueFollowupCount;
    const shouldShowOverdueLine =
      tooltipChartEntry.isToday === true && overdueFollowupCountForTooltip > 0;
    const tooltipOverdueLabelText = shouldShowOverdueLine
      ? `${Format.number(overdueFollowupCountForTooltip)} Overdue`
      : "";

    const nullDueDateFollowupCountForTooltip =
      tooltipChartEntry.nullDueDateFollowupCount;
    const shouldShowNoDueDateLine =
      tooltipChartEntry.isToday === true &&
      nullDueDateFollowupCountForTooltip > 0;
    const tooltipSecondaryLabelText = shouldShowNoDueDateLine
      ? `${Format.number(nullDueDateFollowupCountForTooltip)} No F/U Date`
      : "";

    const hoveredColumnBoundingRectangle =
      hoverPointerEvent.currentTarget.getBoundingClientRect();
    const chartContainerBoundingRectangle =
      chartContainerElement.getBoundingClientRect();

    const nextAnchorLeftPixels =
      hoveredColumnBoundingRectangle.left +
      hoveredColumnBoundingRectangle.width / 2;
    const nextAnchorTopPixels = chartContainerBoundingRectangle.bottom;

    setFollowupChartTooltipState((previousTooltipState) => {
      if (
        previousTooltipState.isVisible === true &&
        previousTooltipState.tooltipDataIndex === hoveredColumnIndex &&
        previousTooltipState.anchorLeftPixels === nextAnchorLeftPixels &&
        previousTooltipState.anchorTopPixels === nextAnchorTopPixels
      ) {
        return previousTooltipState;
      }

      return {
        isVisible: true,
        anchorLeftPixels: nextAnchorLeftPixels,
        anchorTopPixels: nextAnchorTopPixels,
        tooltipTitleText: tooltipTitleText,
        tooltipOverdueLabelText: tooltipOverdueLabelText,
        tooltipPrimaryLabelText: tooltipPrimaryLabelText,
        tooltipSecondaryLabelText: tooltipSecondaryLabelText,
        tooltipDataIndex: hoveredColumnIndex,
      };
    });
  };

  const handleChartContainerPointerLeave = () => {
    setFollowupChartTooltipState((previousTooltipState) => {
      if (previousTooltipState.isVisible === false) {
        return previousTooltipState;
      }
      return { ...previousTooltipState, isVisible: false };
    });
  };

  const datedFollowupBarBackgroundColor =
    materialUiTheme.palette.secondary.main;
  const nullFollowupDateBackgroundColor = materialUiTheme.palette.primary.main;
  const overdueFollowupBarBackgroundColor = materialUiTheme.palette.error.main;

  return (
    <>
      <MUIBox
        ref={chartContainerElementReference}
        onPointerLeave={handleChartContainerPointerLeave}
        sx={{
          position: "absolute",
          bottom: 4,
          left: 0,
          right: (theme) => theme.spacing(1),
          height: 50,
        }}
      >
        <ResponsiveBar
          data={followupCountsBarChartData}
          keys={["noDueDate", "orders", "overdue"]}
          indexBy="weekday"
          margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
          minValue={0}
          maxValue={followupCountChartYAxisSuggestedMaximum}
          padding={0.2}
          groupMode="stacked"
          colors={[
            nullFollowupDateBackgroundColor,
            datedFollowupBarBackgroundColor,
            overdueFollowupBarBackgroundColor,
          ]}
          axisTop={null}
          axisRight={null}
          axisBottom={null}
          axisLeft={null}
          enableGridX={false}
          enableGridY={false}
          enableLabel={false}
          isInteractive={false}
        />
        <MUIBox
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: "flex",
          }}
        >
          {followupCountsChartData.map(
            (followupCountsChartEntry, columnIndex) => (
              <MUIBox
                key={followupCountsChartEntry.dateKey}
                onPointerEnter={(hoverPointerEvent) =>
                  handleColumnPointerEnter(columnIndex, hoverPointerEvent)
                }
                onPointerMove={(hoverPointerEvent) =>
                  handleColumnPointerEnter(columnIndex, hoverPointerEvent)
                }
                sx={{
                  flex: 1,
                  height: "100%",
                }}
              />
            ),
          )}
        </MUIBox>
      </MUIBox>
      {followupChartTooltipState.isVisible === true &&
        ReactDOM.createPortal(
          <MUIBox
            sx={{
              position: "fixed",
              left: `${followupChartTooltipState.anchorLeftPixels}px`,
              top: `${followupChartTooltipState.anchorTopPixels}px`,
              transform: "translate(-50%, 8px)",
              pointerEvents: "none",
              backgroundColor: (theme) => theme.palette.background.paper,
              color: (theme) => theme.palette.text.primary,
              padding: "6px 8px",
              borderRadius: 1,
              fontSize: "0.9rem",
              lineHeight: 1.3,
              whiteSpace: "nowrap",
              zIndex: (theme) => theme.zIndex.tooltip,
              boxShadow: 4,
            }}
          >
            {followupChartTooltipState.tooltipTitleText !== "" && (
              <MUITypography
                component="div"
                variant="subtitle2"
                sx={{ fontWeight: 600 }}
              >
                {followupChartTooltipState.tooltipTitleText}
              </MUITypography>
            )}
            {followupChartTooltipState.tooltipOverdueLabelText !== "" && (
              <MUIBox sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <MUIBox
                  sx={{
                    width: 10,
                    height: 10,
                    backgroundColor: overdueFollowupBarBackgroundColor,
                    flexShrink: 0,
                  }}
                />
                <MUITypography
                  component="span"
                  variant="subtitle2"
                  sx={{ fontWeight: 400 }}
                >
                  {followupChartTooltipState.tooltipOverdueLabelText}
                </MUITypography>
              </MUIBox>
            )}
            <MUIBox sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <MUIBox
                sx={{
                  width: 10,
                  height: 10,
                  backgroundColor: datedFollowupBarBackgroundColor,
                  flexShrink: 0,
                }}
              />
              <MUITypography
                component="span"
                variant="subtitle2"
                sx={{ fontWeight: 400 }}
              >
                {followupChartTooltipState.tooltipPrimaryLabelText}
              </MUITypography>
            </MUIBox>
            {followupChartTooltipState.tooltipSecondaryLabelText !== "" && (
              <MUIBox sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <MUIBox
                  sx={{
                    width: 10,
                    height: 10,
                    backgroundColor: nullFollowupDateBackgroundColor,
                    flexShrink: 0,
                  }}
                />
                <MUITypography
                  component="span"
                  variant="subtitle2"
                  sx={{ fontWeight: 400 }}
                >
                  {followupChartTooltipState.tooltipSecondaryLabelText}
                </MUITypography>
              </MUIBox>
            )}
          </MUIBox>,
          document.body,
        )}
    </>
  );
}
